<template>
    <el-dialog
        :title="(isForm ? (modelId ? '编辑' : '添加') : '查看') + dialogTitle"
        :model-value="modelValue"
        top="10vh"
        width="640px"
        @close="closed"
        :close-on-click-modal="false"
    >
        <el-form
            ref="formRef"
            :model="formData"
            v-loading="loading"
            :rules="isForm ? formRules : {}"
            label-width="150px"
            label-suffix="："
            style="max-width: 560px"
        >
            <!--                IMEI、版本号、ICCID、首次注册时间、标准温度、测量读数、误差温度-->
            <el-form-item label="IMEI" prop="IMEI">
                <el-input v-if="isForm" v-model="formData.IMEI" placeholder="请输入设备IMEI" clearable></el-input>
                <span v-else>{{ formData.IMEI }}</span>
            </el-form-item>
            <el-form-item label="版本" prop="version">
                <span>{{ formData.version }}</span>
            </el-form-item>
            <el-form-item label="ICCID" prop="ICCID">
                <span>{{ formData.ICCID }}</span>
            </el-form-item>
            <el-form-item label="首次注册时间" prop="reg_ts">
                <span>{{ formData.reg_ts }}</span>
            </el-form-item>
            <el-form-item label="标准温度" prop="standard_temp">
                <el-input v-if="isForm" v-model="formData.standard_temp" placeholder="请输入标准温度" clearable></el-input>
                <span>{{ formData.standard_temp }}</span>
            </el-form-item>
            <el-form-item label="测量读数" prop="measure_temp">
                <el-input v-if="isForm" v-model="formData.measure_temp" placeholder="请输入测量读数" clearable></el-input>
                <span>{{ formData.measure_temp }}</span>
            </el-form-item>
            <el-form-item label="误差温度" prop="error_temp">
                <el-input v-if="isForm" v-model="formData.error_temp" placeholder="请输入误差温度" clearable></el-input>
                <span>{{ formData.error_temp }}</span>
            </el-form-item>
        </el-form>
        <template #footer>
            <span class="dialog-footer" v-if="isForm">
                <el-button @click="closed">取消</el-button>
                <el-button type="primary" :loading="submitLoading" @click="onConfirm">确定</el-button>
            </span>
            <span class="dialog-footer" v-else>
                <el-button type="primary" @click="closed">关闭</el-button>
            </span>
        </template>
    </el-dialog>
</template>
<script setup>
import { defineEmits, defineProps, onMounted, ref } from 'vue'
import { editDeviceInfo } from '@/api/heating/devicePoint/devicePoint.js'
import { ElMessage } from 'element-plus'
import { getOptionTree } from '@/api/room.js'
import { getEquipModelTree } from '@/api/equipment.js'

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    isForm: {
        type: Boolean,
        default: false
    },
    modelId: {
        type: String,
        required: false
    },
    detailData: {
        type: Object,
        required: false
    }
})
const emits = defineEmits(['update:modelValue', 'updateForm'])

// 型号列表
const model_tree = ref([])
const model_list = ref({})
async function getModelList() {
    await getEquipModelTree().then((data) => {
        model_tree.value = data
        const list = {}
        data.forEach((device_type) => {
            device_type?.children &&
                device_type.children.forEach((device_model) => {
                    list[device_model.id] = {
                        type: device_type.name,
                        model: device_model.name
                    }
                })
        })
        model_list.value = list
    })
}

// 安装位置
const room_tree = ref([])
const room_list = ref({})
async function getRoomTree() {
    await getOptionTree().then((data) => {
        room_tree.value = data
        const room_locations = {}
        function getRoom(list, location) {
            list.forEach((item) => {
                if (item.children) {
                    getRoom(item.children, location + '/' + item.name)
                } else {
                    room_locations[item.id] = location.slice(1) + '/' + item.name
                }
            })
        }
        getRoom(data, '')
        room_list.value = room_locations
    })
}

// 表单源
const formData = ref({
    id: '',
    name: '',
    model: '',
    IMEI: '',
    building: '',
    room: '',
    location: '',
    version: '',
    ICCID: ''
})

// 验证规则

const formRules = ref({
    IMEI: [
        {
            required: true,
            trigger: 'blur',
            validator: (rule, value, callback) => {
                // IMEI必须为15位纯数字，且为必填项
                if (value === '') {
                    callback(new Error('请输入设备IMEI'))
                } else if (!/^[0-9]{15}$/.test(value)) {
                    callback(new Error('IMEI必须为15位纯数字'))
                } else {
                    callback()
                }
            }
        }
    ]
})
const dialogTitle = ref('')
const submitLoading = ref(false)
const formRef = ref(null)

// 关闭
const closed = () => {
    dialogTitle.value = ''
    document.querySelector('.el-dialog__body').scrollTo(0, 0)
    emits('update:modelValue', false)
    formRef.value && formRef.value.resetFields()
}

// 确定按钮点击事件
const onConfirm = async () => {
    if (!props.isForm) return
    if (props.modelId !== '') {
        await handleEdit()
    }
}

// 修改
const handleEdit = async () => {
    // 进行表单校验
    formRef.value.validate(async (valid) => {
        if (!valid) return
        submitLoading.value = true
        const data = { type: 2, ...formData.value }
        try {
            await editDeviceInfo(data)
            // 模块修改成功
            emits('updateForm')
            ElMessage.success('修改成功！')
            submitLoading.value = false
            closed()
        } catch {
            submitLoading.value = false
        }
    })
}

const loading = ref(false)

onMounted(async () => {
    loading.value = true
    await getModelList()
    await getRoomTree()

    if (props.modelId) {
        const obj = { ...formData.value }
        Object.keys(props.detailData).forEach((key) => {
            if (key in obj) {
                obj[key] = props.detailData[key]
            }
        })
        formData.value = obj
        loading.value = false
    } else {
        formData.value = {
            id: '',
            name: '',
            model: '',
            IMEI: '',
            building: '',
            room: '',
            version: '',
            ICCID: ''
        }
    }
})
</script>

<style lang="scss" scoped>
.albums-lists {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;

    .img-item {
        width: 100px;
        height: 100px;
        background-color: #999;
        border-radius: 5px;
        margin-right: 10px;
    }
}

.albums-upload-lists {
    :deep(.el-upload-list__item),
    :deep(.el-upload--picture-card) {
        width: 100px;
        height: 100px;

        .avatar-uploader-icon {
            margin-top: 36px;
        }
    }

    :deep(.el-upload-list__item-preview),
    :deep(.el-icon--zoom-in) {
        display: none;
    }
}

.files-upload-lists {
    :deep(.el-upload-list__item),
    :deep(.el-upload--picture-card) {
        width: 100%;
        height: auto;
    }
}
body {
    margin: 0;
}
.example-showcase .el-loading-mask {
    z-index: 9;
}
</style>
